import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { twMerge } from "tailwind-merge";
import {
  FiArrowLeft,
  FiMapPin,
  FiDatabase,
  FiUsers,
  FiClipboard,
  FiCalendar,
  FiCreditCard,
  FiChevronRight,
  FiActivity,
  FiLogOut,
  FiMenu,
  FiX,
  FiHelpCircle,
  FiChevronDown,
  FiChevronUp,
  FiPlay
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Validador from "./valida/Validador";
import { startTour, stopTour } from "@ipejal/tour-manager";
import "driver.js/dist/driver.css";

export default function RevealBento({ user, activeView, setActiveView }) {
  const navigate = useNavigate();
  const [tipoPrestamos, setTipoPrestamos] = useState([]);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const videoRef = useRef(null);

  const chapters = [
    { title: "Login", start: 0, end: 20, timeStr: "00:00" },
    { title: "Mi Cuenta IPEJAL", start: 20, end: 26, timeStr: "00:20" },
    { title: "Tipos de Trámites", start: 26, end: 43, timeStr: "00:26" },
    { title: "Mis Folios, Requisitos e Inicio de Trámite", start: 43, end: 68, timeStr: "00:43" },
    { title: "Indicaciones y Subir Documentos", start: 68, end: 117, timeStr: "01:08" },
    { title: "En Espera de Validación", start: 117, end: 127, timeStr: "01:57" },
    { title: "Correo de Prevalidador", start: 127, end: 143, timeStr: "02:07" },
    { title: "Resubir Documentos", start: 143, end: 196, timeStr: "02:23" },
    { title: "Correo Prevalidador Aprobado", start: 196, end: 214, timeStr: "03:16" },
    { title: "Agendar Cita", start: 214, end: 278, timeStr: "03:34" }
  ];

  useEffect(() => {
    const checkScrollable = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      setIsScrollable(documentHeight > windowHeight + 30);
    };

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      const scrollable = documentHeight > windowHeight + 30;
      setIsScrollable(scrollable);

      // If we are close to the bottom of the page
      if (scrollable && (scrollTop + windowHeight >= documentHeight - 120)) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    };

    // Run initially and after a delay to ensure items have rendered in DOM
    checkScrollable();
    const timer = setTimeout(checkScrollable, 600);

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", checkScrollable);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkScrollable);
    };
  }, [tipoPrestamos]);

  const handleScrollStep = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollable = documentHeight > windowHeight + 30;

    if (scrollable && isAtBottom) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const items = document.querySelectorAll(".tramite-item");
    if (items.length === 0) return;

    const headerHeight = 100;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    let targetItem = null;

    for (let i = 0; i < items.length; i++) {
      const elementTop = items[i].getBoundingClientRect().top + scrollTop;
      // Find the first item that is below current scroll position + header offset + margin
      if (elementTop > scrollTop + headerHeight + 5) {
        targetItem = items[i];
        break;
      }
    }

    if (targetItem) {
      const elementTop = targetItem.getBoundingClientRect().top + scrollTop;
      window.scrollTo({
        top: elementTop - headerHeight,
        behavior: "smooth"
      });
    } else {
      if (scrollable) {
        window.scrollTo({ top: documentHeight, behavior: "smooth" });
      }
    }
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setCurrentVideoTime(0);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace('/');
  };

  const handleStartTour = (force = true) => {
    const tourJSON = {
      steps: [
        {
          target: "titulo-tramites",
          title: "Tus Trámites",
          description: "Aquí puedes iniciar y consultar tus solicitudes de préstamos de forma digital.",
          position: "bottom"
        },
        {
          target: "lista-tramites",
          title: "Lista de Trámites",
          description: "Elige el tipo de préstamo que deseas solicitar.",
          position: "top"
        },
        {
          target: "primer-tramite",
          title: "Iniciar Solicitud",
          description: "Presiona el préstamo que necesitas para comenzar a subir tus documentos.",
          position: "top"
        }
      ],
      options: {
        persistKey: "tour-catalog",
        showProgress: true,
        allowClose: true,
        doneBtnText: "Entendido",
        nextBtnText: "Siguiente Paso",
        prevBtnText: "Volver Atrás"
      }
    };

    try {
      startTour(tourJSON, force);
    } catch (error) {
      console.error("Error al iniciar el tour:", error);
    }
  };

  useEffect(() => {
    if (activeView === "home" && tipoPrestamos.length > 0) {
      const isCompleted = localStorage.getItem("tour-catalog");
      if (isCompleted !== "completed") {
        const timer = setTimeout(() => {
          handleStartTour(false);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [activeView, tipoPrestamos]);

  useEffect(() => {
    let storedUser = null;
    try {
      const storedUserRaw = localStorage.getItem("user");
      if (storedUserRaw) {
        storedUser = JSON.parse(storedUserRaw);
      }
    } catch (e) {
      console.error("Error reading/parsing user from localStorage:", e);
    }

    const endpoint = storedUser?.userType?.toUpperCase() === "DEUDO" ? "getTipoPrestamosSinLogin" : "tipoPrestamos";

    fetch(`${apiUrl}/${endpoint}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.data) {
          setTipoPrestamos(
            json.data.filter(
              (t) =>
                t.es_activo &&
                t.id_catalogo_tramite !== 0 &&
                t.id_catalogo_tramite !== "0" &&
                Number(t.id_tipo_prestamo) !== 7
            )
          );
        }
      })
      .catch((e) => {
        console.error(`Error fetching ${endpoint}:`, e);
      });
  }, [apiUrl]);

  if (activeView !== "home") {
    const prestamoData = tipoPrestamos.find(
      (t) => t.nombre === activeView
    );
    if (!prestamoData) return null;

    return (
      <PrestamoView
        onBack={() => setActiveView("home")}
        tipoPrestamo={prestamoData.nombre}
        nombreDisplay={prestamoData.descripcion}
        user={user}
      />

    );
  }

  const getIcon = (name) => {
    const n = name.toUpperCase();
    if (n.includes("DB")) return <FiDatabase className="text-blue-500" />;
    if (n.includes("ATENCIÓN") || n.includes("AP")) return <FiUsers className="text-orange-500" />;
    if (n.includes("ESTUDIO") || n.includes("EC")) return <FiClipboard className="text-red-500" />;
    if (n.includes("JUBILACIÓN") || n.includes("PH")) return <FiCalendar className="text-purple-500" />;
    if (n.includes("CREDENCIAL") || n.includes("PMP")) return <FiCreditCard className="text-green-500" />;
    return <FiActivity className="text-primary" />;
  };
  return (
    <div className="min-h-screen bg-slate-50/50 text-[#1a1a1a] font-sans pb-20">
      {/* Refined Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center h-20 px-6">
        <div className="w-full flex items-center justify-between">
          <motion.img
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            src="/images/index/logo.png"
            alt="IPEJAL"
            className="h-8 md:h-10 w-auto object-contain"
          />
          <div className="flex items-center gap-2 md:gap-3">
            <div className="flex flex-col items-end mr-1 md:mr-4 text-right">
              <span className="hidden md:block text-[10px] font-black uppercase tracking-[0.15em] text-primary">Portal Afiliados</span>
              <div className="flex flex-col items-end">
                <span className="text-[11px] md:text-xs font-bold text-gray-800 tracking-tight">
                  {user?.displayName || `${user?.firstName || ""} ${user?.firstLastName || ""} ${user?.secondLastName || ""}`.trim() || "Usuario"}
                </span>
                <span className="text-[9px] md:text-[10px] text-gray-500 font-semibold tracking-tight">
                  {[
                    user?.username ? `Usuario: ${user.username}` : null,
                    user?.rfc ? `RFC: ${user.rfc}` : null,
                    user?.email ? user.email : null
                  ].filter(Boolean).join(" | ")}
                </span>
              </div>
            </div>
            <button
              onClick={handleStartTour}
              className="flex items-center justify-center gap-1.5 px-3.5 h-10 md:h-11 rounded-xl text-primary hover:bg-primary/5 transition-all active:scale-90 bg-gray-50 border border-slate-100 font-bold text-[11px] shadow-sm uppercase tracking-wider"
              title="Ver guía rápida"
            >
              <FiHelpCircle className="text-sm" />
              <span className="hidden md:inline">Ver Guía</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center justify-center size-10 md:size-12 rounded-2xl text-red-500 hover:bg-red-50 transition-all active:scale-90 bg-gray-50 md:bg-transparent shadow-sm md:shadow-none"
              title="Cerrar Sesión"
            >
              <FiLogOut className="text-lg md:text-xl" />
            </button>
          </div>

        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6 md:py-10">
        <div className="mb-6 flex flex-row items-start gap-3 md:gap-5" data-tour="titulo-tramites">
          <div className="shrink-0 pt-0.5 md:pt-1.5">
            <button
              onClick={() => setShowVideoModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 md:px-3.5 md:py-2.5 rounded-xl bg-primary hover:bg-primary_light text-white font-bold text-[10px] md:text-xs shadow-md shadow-primary/20 transition-all active:scale-95 cursor-pointer uppercase tracking-wider"
              title="Ver video tutorial"
            >
              <FiPlay className="text-xs md:text-sm fill-current" />
              <span>Video Guía</span>
            </button>
          </div>
          <div className="space-y-1 flex-1">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl md:text-3xl font-bold tracking-tight text-[#1a1a1a] leading-tight"
            >
              ¿Qué trámite <br className="md:hidden" /> <span className="text-primary">deseas realizar?</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-sm md:text-base text-gray-500 font-normal max-w-xl"
            >
              Selecciona una opción de la lista para iniciar tu preregistro de forma digital.
            </motion.p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 gap-4"
          data-tour="lista-tramites"
        >
          {tipoPrestamos.map((service, idx) => (
            <ServiceListItem
              key={service.id_tipo_prestamo}
              service={service}
              index={idx}
              dataTour={idx === 0 ? "primer-tramite" : undefined}
              onClick={() => {
                stopTour(); // Cierra el tour para evitar que se quede colgado al desmontar el componente
                setActiveView(service.nombre);
                localStorage.setItem("id_tipo_prestamo", service.id_tipo_prestamo);
                localStorage.setItem("id_catalogo_tramite", service.id_catalogo_tramite);
                localStorage.removeItem("id_sede");
              }}
            />
          ))}


        </motion.div>
      </main>

      {/* Floating Action Button */}
      <AnimatePresence>
        {tipoPrestamos.length > 0 && isScrollable && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleScrollStep}
            className="fixed bottom-6 right-6 z-50 flex items-center justify-center size-14 rounded-full bg-gradient-to-tr from-primary to-primary_light text-white shadow-lg shadow-primary/30 border border-white/20 cursor-pointer focus:outline-none hover:shadow-primary/40 transition-shadow active:scale-95"
            title={isAtBottom ? "Volver al inicio" : "Siguiente trámite"}
          >
            {isAtBottom ? (
              <FiChevronUp className="text-2xl animate-bounce" style={{ animationDuration: '2.5s' }} />
            ) : (
              <FiChevronDown className="text-2xl animate-bounce" style={{ animationDuration: '2.5s' }} />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Video Modal Overlay */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={handleCloseVideoModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl overflow-hidden shadow-2xl max-w-7xl w-full border border-slate-100 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold shadow-inner">
                    <FiPlay className="text-sm fill-current" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">Video Guía de Trámites</h3>
                    <p className="text-[10px] text-gray-400 font-semibold tracking-tight">Digitalización de Préstamos IPEJAL</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseVideoModal}
                  className="size-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <FiX className="text-xl" />
                </button>
              </div>

              {/* Modal Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-4">
                {/* Left Side: Video Player (Takes 3 of 4 columns) */}
                <div className="lg:col-span-3 bg-[#0d0c15] aspect-video flex items-center justify-center relative overflow-hidden">
                  <video
                    ref={videoRef}
                    src="/VideoPrevalidadorAfiliado.mp4"
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                    onTimeUpdate={(e) => setCurrentVideoTime(e.target.currentTime)}
                  />
                </div>

                {/* Right Side: Chapter Markers (Takes 1 of 4 columns) */}
                <div className="flex flex-col h-[300px] lg:h-auto border-t lg:border-t-0 lg:border-l border-slate-100 bg-slate-50/50">
                  <div className="p-4 border-b border-slate-100 bg-white">
                    <span className="text-[10px] font-black text-primary uppercase tracking-[0.15em]">Índice de Contenido</span>
                    <h4 className="text-[11px] text-gray-500 font-medium tracking-tight mt-0.5">Presiona un tema para saltar al video</h4>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-3 space-y-1.5 scrollbar-thin">
                    {chapters.map((ch, idx) => {
                      const isActive = currentVideoTime >= ch.start && currentVideoTime < ch.end;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            if (videoRef.current) {
                              videoRef.current.currentTime = ch.start;
                              videoRef.current.play();
                            }
                          }}
                          className={twMerge(
                            "w-full flex items-start gap-3 p-2.5 rounded-2xl text-left transition-all duration-200 border cursor-pointer",
                            isActive
                              ? "bg-primary/10 border-primary/20 text-primary font-bold shadow-sm shadow-primary/5"
                              : "hover:bg-white border-transparent text-gray-600 hover:text-gray-900 hover:shadow-sm"
                          )}
                        >
                          <div className={twMerge(
                            "size-6 rounded-lg flex items-center justify-center shrink-0 text-[10px] font-black shadow-sm border transition-colors",
                            isActive
                              ? "bg-primary text-white border-primary"
                              : "bg-white text-gray-400 border-slate-100"
                          )}>
                            {isActive ? (
                              <FiPlay className="text-[8px] fill-current animate-pulse" />
                            ) : (
                              idx + 1
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] leading-snug truncate">
                              {ch.title}
                            </p>
                            <span className={twMerge(
                              "text-[9px] font-semibold tracking-wider",
                              isActive ? "text-primary/75" : "text-gray-400"
                            )}>
                              {ch.timeStr}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ServiceListItem = ({ service, onClick, index, dataTour }) => {
  const initial = service.nombre ? service.nombre.charAt(0).toUpperCase() : "?";

  return (
    <motion.button
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 + (index * 0.05) }}
      onClick={onClick}
      data-tour={dataTour}
      className="group flex items-center p-6 bg-white rounded-[2rem] border border-slate-200 shadow-sm hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5 transition-all active:scale-[0.98] text-left gap-6 w-full tramite-item"
    >
      <div className="shrink-0 size-12 md:size-14 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100 font-bold text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all text-xl md:text-2xl shadow-inner">
        {initial}
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="text-lg md:text-xl font-bold tracking-tight text-[#1a1a1a] group-hover:text-primary transition-colors truncate mb-1">
          {service.nombre}
        </h3>
        <p className="text-sm text-gray-500 font-medium leading-tight line-clamp-2">
          {service.descripcion}
        </p>
      </div>

      <div className="shrink-0 text-gray-200 group-hover:text-primary transition-all transform group-hover:translate-x-1">
        <FiChevronRight className="text-3xl" />
      </div>
    </motion.button>
  );
};

const PrestamoView = ({ onBack, tipoPrestamo, nombreDisplay, user }) => (
  <div className="min-h-screen bg-slate-50/80 flex flex-col">
    <header className="sticky top-0 z-50 bg-white border-b border-slate-100 flex items-center h-20 px-6 shadow-sm">
      <div className="w-full flex items-center justify-between">
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            stopTour(); // Cierra el tour si estaba activo en el módulo de trámites
            onBack();
          }}
          className="flex items-center gap-2 text-primary font-bold text-xs hover:bg-primary/5 px-4 py-2 rounded-xl transition-all active:scale-95 group"
        >
          <FiArrowLeft className="text-base group-hover:-translate-x-1 transition-transform" />
          <span className="uppercase tracking-widest mt-0.5">Volver</span>
        </motion.button>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex flex-col items-end mr-1 md:mr-0 text-right">
            <span className="hidden md:block text-[9px] font-bold text-primary uppercase tracking-[0.15em]">IPEJAL Digital</span>
            <div className="flex flex-col items-end">
              <span className="text-[11px] md:text-xs font-bold text-gray-800 tracking-tight">
                {user?.displayName || `${user?.firstName || ""} ${user?.firstLastName || ""} ${user?.secondLastName || ""}`.trim() || "Usuario"}
              </span>
              <span className="text-[9px] md:text-[10px] text-gray-500 font-semibold tracking-tight">
                {[
                  user?.username ? `Usuario: ${user.username}` : null,
                  user?.rfc ? `RFC: ${user.rfc}` : null,
                  user?.email ? user.email : null
                ].filter(Boolean).join(" | ")}
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.clear();
              window.location.replace('/');
            }}
            className="size-10 md:size-11 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-xl transition-colors bg-gray-50 md:bg-transparent shadow-sm md:shadow-none"
            title="Cerrar Sesión"
          >
            <FiLogOut className="text-lg md:text-xl" />
          </button>
        </div>

      </div>
    </header>

    <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
        {tipoPrestamo && (
          <Validador tipoPrestamo={tipoPrestamo} nombreLargo={nombreDisplay} isPreregistro={true} />
        )}
      </div>
    </main>
  </div>
);

const Logo = () => (
  <div className="hidden">
    {/* Usado en el sticky header ahora */}
  </div>
);
